/**
 * 数据看板 - 接口定义
 */
import { apiDownload, apiGet } from '@/utils/request';
import type {
  AllocationStatsItem,
  DashboardStatsParams,
  DashboardStatsResponse,
  GuideStatsResponse,
  LeadStatsItem,
  ServingStatsItem,
} from '@/types/dashboardReception';
import type { AgentStatsParams, AgentStatsResponse } from '@/types/dashboardAgentStats';
import type { DashboardAllData } from '@/types/dashboard';
import type { MsgReplyStatParams, MsgReplyStatResponse } from '@/types/dashboardDyIsv';

export const dashboardApi = {
  config: (params?: Record<string, unknown>) => apiGet('/dashboard/config', params),
  all: (params?: Record<string, unknown>) => apiGet<DashboardAllData>('/dashboard/all', params),
  /** 账户数据导出：复用列表接口并加传 export=1 */
  accountExport: (params?: Record<string, unknown>) =>
    apiDownload('/dashboard/all', {
      params: { ...params, export: 1 },
      filename: '账户数据.xlsx',
    }),
  summary: (params?: Record<string, unknown>) => apiGet('/dashboard/summary', params),
  trend: (params?: Record<string, unknown>) => apiGet('/dashboard/trend', params),
  privateMessages: (params?: Record<string, unknown>) =>
    apiGet('/dashboard/private-messages', params),
  /** 留资数据看板：账号 / 渠道 / 机器人维度 */
  leadStats: (params: DashboardStatsParams) =>
    apiGet<DashboardStatsResponse<LeadStatsItem>>('/dashboard/lead-stats', params),
  /** 接待数据看板：坐席 / 坐席分组 / 日期维度 */
  servingStats: (params: DashboardStatsParams) =>
    apiGet<DashboardStatsResponse<ServingStatsItem>>('/dashboard/serving-stats', params),
  /** 分配数据看板：坐席 / 坐席分组 / 日期维度 */
  allocationStats: (params: DashboardStatsParams) =>
    apiGet<DashboardStatsResponse<AllocationStatsItem>>('/dashboard/allocation-stats', params),
  /** 导购数据看板：仅统计抖音渠道，支持日期、渠道账号与机器人维度。 */
  guideStats: (params: DashboardStatsParams) =>
    apiGet<GuideStatsResponse>('/guide-stat/statistics', params),
  /** 导购数据导出：复用列表接口并加传 export=1 */
  guideStatsExport: (params: DashboardStatsParams) =>
    apiDownload('/guide-stat/statistics', {
      params: { ...params, export: 1 },
      filename: '导购数据.xlsx',
    }),
  /** 坐席接待数据：接待、留资与在线时长统计，支持坐席 / 分组 / 渠道 / 账号维度 */
  agentReceptionStats: (params: AgentStatsParams) =>
    apiGet<AgentStatsResponse>('/dashboard/agent-stats/reception', params),
  /** 坐席接待数据导出：复用列表接口并加传 export=1 */
  agentReceptionStatsExport: (params: AgentStatsParams) =>
    apiDownload('/dashboard/agent-stats/reception', {
      params: { ...params, export: 1 },
      filename: '坐席接待数据.xlsx',
    }),
  /** 坐席接入统计：开口与留资统计，支持账号 / 渠道 / 日期维度 */
  agentAccessStats: (params: AgentStatsParams) =>
    apiGet<AgentStatsResponse>('/dashboard/agent-stats/access', params),
  /** 坐席介入统计导出：复用列表接口并加传 export=1 */
  agentAccessStatsExport: (params: AgentStatsParams) =>
    apiDownload('/dashboard/agent-stats/access', {
      params: { ...params, export: 1 },
      filename: '坐席介入统计.xlsx',
    }),
  /** 坐席回复统计：首响与咨询超时统计，支持坐席 / 分组 / 渠道 / 账号维度 */
  agentReplyStats: (params: AgentStatsParams) =>
    apiGet<AgentStatsResponse>('/dashboard/agent-stats/reply', params),
  /** 坐席回复统计导出：复用列表接口并加传 export=1 */
  agentReplyStatsExport: (params: AgentStatsParams) =>
    apiDownload('/dashboard/agent-stats/reply', {
      params: { ...params, export: 1 },
      filename: '坐席回复统计.xlsx',
    }),
  /** 抖音ISV报表：回复时效统计（T+1，只统计当前坐席可见的渠道账号） */
  msgReplyStats: (params: MsgReplyStatParams) =>
    apiGet<MsgReplyStatResponse>('/dashboard/msg-reply-stats', params),
};
